import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid2 as Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

// helper
import ReactSwal from "../../helper/AlertHelper";
import {
  maxUploadFileMb,
} from "../../helper/GlobalValueHelper";
import { resizeFile } from "../../helper/UnitsHelper";

// icons
import MoreVertIcon from "@mui/icons-material/MoreVert";

// components
import DebugLogger from "../DebugLogger";
import ImageUploader from "../ImageUploader";
import UpdaterInfo from "../UpdaterInfo";
import ValidationTextField from "../ValidationTextField";

export default function BaseInfo({
  formik,
  onDelete,
  haveDeletePermission,
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [images, setImages] = useState([]);
  const [showImageUploader, setShowImageUploader] = useState(false);

  useEffect(() => {
    let newImages = [];

    if (formik.values.photo) {
      newImages = [...images, formik.values.photo];
    }

    if (formik.values.uploadFiles) {
      formik.values.uploadFiles.forEach(file => {
        newImages.push(URL.createObjectURL(file));
      });
    }

    setImages(newImages);
  }, [JSON.stringify(formik.values)]);

  useEffect(() => {
    if (!formik.values.photo) {
      setShowImageUploader(true);
      return;
    }
    let imageLength = 1;
    imageLength += formik.values.uploadFiles?.length || 0;

    let variantLength = 1;

    if (imageLength >= variantLength) {
      setShowImageUploader(false);
    } else {
      setShowImageUploader(true);
    }
  }, [JSON.stringify(formik.values)]);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const showDeleteConfirm = () => {
    ReactSwal.fire({
      title: t("error.warning"),
      text: t("error.delete_confirm"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("layout.ok"),
      cancelButtonText: t("layout.cancel"),
    }).then(result => {
      if (result.isConfirmed) {
        onDelete();
      }
    });
  };

  const onImageChange = async images => {
    let uploadFiles = formik.values.uploadFiles || [];

    // check if file exists
    let fileExists = false;
    uploadFiles.forEach(file => {
      images.forEach(image => {
        if (file.name === image.file.name) {
          fileExists = true;
          return;
        }
      });
    });

    if (fileExists) {
      return;
    }

    if (images && images.length > 0) {
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        if (image.file.size > 1024 * 1024 * maxUploadFileMb) {
          toast.error(`Image ${index + 1} file size must be less than ${maxUploadFileMb}MB`);
          return;
        }

        const resizedImage = await resizeFile({
          file: image.file,
        });

        uploadFiles.push(resizedImage);
      }
    }

    formik.setFieldValue("uploadFiles", uploadFiles);
    images = null;
  };

  const onRemoveImage = () => {
    ReactSwal.fire({
      title: t("error.warning"),
      text: t("error.delete_confirm"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("layout.ok"),
      cancelButtonText: t("layout.cancel"),
    }).then(result => {
      if (result.isConfirmed) {
        formik.setFieldValue("photo", "");
        formik.setFieldValue("uploadFiles", []);
        setImages([]);
      }
    });
  };

  const removeUploadFile = index => {
    ReactSwal.fire({
      title: t("error.warning"),
      text: t("error.delete_confirm"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("layout.ok"),
      cancelButtonText: t("layout.cancel"),
    }).then(result => {
      if (result.isConfirmed) {
        let newUploadFiles = formik.values.uploadFiles;
        newUploadFiles.splice(index, 1);
        formik.setFieldValue("uploadFiles", newUploadFiles);
      }
    });
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 5,
      }}
    >
      <CardHeader
        title={t("layout.member_general")}
        action={
          onDelete &&
          haveDeletePermission && (
            <Box>
              <IconButton onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem onClick={showDeleteConfirm}>
                  {<Typography color={"error"}>{t("layout.delete")}</Typography>}
                </MenuItem>
              </Menu>
            </Box>
          )
        }
      />

      <CardContent>
        <DebugLogger title="Members" data={formik.values} hidden={true}></DebugLogger>

        <Typography
          sx={{
            pb: 2,
          }}
        >
          {t("member.profile_photo")}
        </Typography>

        <Box
          sx={{
            mb: 2,
            fontWight: "bold",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Typography variant="caption">
            {t("page.maximum_upload_size")}: {maxUploadFileMb} MB
          </Typography>
          <Typography variant="caption">
            {t("page.accept_file_type")}: JPEG / JPG / PNG / WEBP / SVG
          </Typography>
        </Box>
        <Stack spacing={2} >
          {formik.values.photo && (
            <Stack>
              <Box
                component={"img"}
                src={formik.values.photo}
                sx={{
                  objectFit: "contain",
                  maxWidth: "100%",
                  maxHeight: "200px",
                }}
              />
              <Button
                sx={{
                  p: 2,
                }}
                onClick={() => {
                  onRemoveImage();
                }}
              >
                {t("layout.remove")}
              </Button>
            </Stack>
          )}

          {formik.values.uploadFiles &&
            formik.values.uploadFiles.length > 0 &&
            formik.values.uploadFiles.map((file, index) => (
              <Stack key={index}>
                <Box
                  component={"img"}
                  src={URL.createObjectURL(file)}
                  sx={{
                    objectFit: "contain",
                    maxWidth: "100%",
                    maxHeight: "200px",
                  }}
                />
                <Button
                  sx={{
                    p: 2,
                  }}
                  onClick={() => {
                    removeUploadFile(index);
                  }}
                >
                  {t("layout.remove")}
                </Button>
              </Stack>
            ))}
          {showImageUploader && (
            <ImageUploader
              maxNumber={1}
              images={images}
              maxFileSize={1024 * 1024 * maxUploadFileMb}
              onImageChange={onImageChange}
              onRemoveImage={onRemoveImage}
              removeAllButton={false}
            />
          )}

          <ValidationTextField
            name={"phoneNumber"}
            type={"text"}
            value={formik.values.phoneNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            title={t("member.phone_number")}
            errorText={formik.errors.phoneNumber}
            touched={formik.touched.phoneNumber}
          />

          <ValidationTextField
            name={"email"}
            type={"text"}
            required={true}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            title={t("member.email")}
            errorText={formik.errors.email}
            touched={formik.touched.email}
          />

        </Stack>

        <Grid container spacing={2} sx={{
          py: 2
        }}>
          <Grid size={{
            xs: 12,
            md: 6
          }}>
            <ValidationTextField
              name={"firstName"}
              type={"text"}
              value={formik.values.firstName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              title={t("member.first_name")}
              errorText={formik.errors.firstName}
              touched={formik.touched.firstName}
            />
          </Grid>

          <Grid size={{
            xs: 12,
            md: 6
          }}>
            <ValidationTextField
              name={"lastName"}
              type={"text"}
              value={formik.values.lastName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              title={t("member.last_name")}
              errorText={formik.errors.lastName}
              touched={formik.touched.lastName}
            />
          </Grid>
        </Grid>

        <Stack spacing={2}>
          <ValidationTextField
            name={"memberLevel"}
            type={"number"}
            value={formik.values.memberLevel || 0}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            title={t("member.member_level")}
            errorText={formik.errors.memberLevel}
            touched={formik.touched.memberLevel}
          />

          <ValidationTextField
            name={"remark"}
            type={"text"}
            multiline={true}
            value={formik.values.remark}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            title={t("member.remark")}
            errorText={formik.errors.remark}
            touched={formik.touched.remark}
          />


          <UpdaterInfo data={formik.values} />
        </Stack>
      </CardContent>
      <CardActions sx={{ justifyContent: "space-between", px: 2 }}></CardActions>
    </Card>
  );
}
